import * as React from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import {
  Chip,
  TableCell,
  TableRow,
  Popper,
  Box,
  Paper,
  Typography,
  TextField,
  Divider,
  Stack,
  FormHelperText,
  FormControl,
  Tooltip,
  TableBody,
  Table,
  IconButton} from '@mui/material';
import InputError, { InputErrorProps } from './InputError';
import { useAutoSubmitSignal } from 'hooks/autosubmit';
import { useFieldInfo } from 'contexts/FieldInfoContext';
import SearchIcon from '@mui/icons-material/Search';
import { filterTableOptions, getColumns, isSelectedOption, isSelectedValue, selectOption } from 'utils/option-utils';
import { isEmptyValue, useDebounce } from 'utils/utils';
import { validateField } from '../utils/validate-utils';
import { Option } from '../../../types/option';
import { useRef } from 'react';
import { useTranslator } from 'hooks/translator';
import DeleteIcon from '@mui/icons-material/Delete';
import { cloneRow, focusRef, reorder, Results, rowIcon } from './select/common';
import { KeyboardDoubleArrowUp, Visibility, VisibilityOff } from '@mui/icons-material';
import { DragDropContext, Draggable, DraggableProvided, Droppable } from '@hello-pangea/dnd';


function InputMultipleEntityFieldLocalContent(props: InputErrorProps) {
  const { augProps, fieldProps, info } = useFieldInfo()

  const filterRef                                       = useRef<HTMLDivElement>(null);
  const [input, setInput, debouncedInput, skipDebounce] = useDebounce("", 500)
  const [selected, setSelected]                         = React.useState<Option[]>([])
  const [anchorEl, setAnchorEl]                         = React.useState<null | HTMLElement>(null);
  const [options, setOptions]                           = React.useState<Option[]>(info.options || []);
  const [loading, setLoading ]                          = React.useState(false)
  const [showSelected, setShowSelected]                 = React.useState<boolean>(true)
  const { setFocus, setViewOpen }                       = props
  const { signal }                                      = useAutoSubmitSignal()
  const { t }                                           = useTranslator()
  const open = Boolean(anchorEl);
  
  function openPopper(elem: HTMLElement | null) {
    setAnchorEl(elem)
    setViewOpen(true)
    setFocus(true)
    focusRef(filterRef)
  }

  function closePopper() {
    setFocus(false)
    setViewOpen(false)
    setAnchorEl(null)
    handleValidate(fieldProps.value)
  }

  React.useEffect(() => {
    const test = fieldProps.value && Array.isArray(fieldProps.value);
    setSelected(test ? fieldProps.value : [])
    
    // translate options
    // selected = formatChoices(translator, selected, info.optionsKind, formInfo.processDefinition.key)
  }, [fieldProps.value]);

  React.useEffect(() => {
    if (!(open))
      return;

    setLoading(true)

    let active = true;
    (async () => {
      if (!info.options) return
      const options = info.options
      const filteredOptions = isEmptyValue(debouncedInput)
        ? options
        : filterTableOptions(options, debouncedInput)

      if (active) {
        setOptions(filteredOptions)
        setLoading(false)
      }
    })();

    return () => { active = false; }
  }, [open, debouncedInput]);

  React.useEffect(() => {
    if (!open) {
      setOptions([]);
      setLoading(false)
    } else {
      if (input !== debouncedInput)
        setLoading(true)

      skipDebounce()
    }
  }, [input, open])

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    openPopper(e.currentTarget.parentElement);
    e.stopPropagation()
  }
  const handleDelete = (e: React.SyntheticEvent) => {
    setSelected([])
    augProps.setValue([])
    handleValidate([], e)
  }

  function handleBlur (e: any) {
    const id = fieldProps.id
    fieldProps.onBlur({id: id, target: document.getElementById(id), ...e})
    closePopper()
    signal()
  }

  function handleFocus(e: React.SyntheticEvent) {
    setFocus(true)
    fieldProps.onFocus({id: fieldProps.id, ...e})
  }

  function handleValidate(options: Option[], e?: React.SyntheticEvent) {
    const error = validateField("multiselect", fieldProps.required, e || null, options)
    augProps.setError(error)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    switch (e.key) {
      case "Escape":
        e.preventDefault()
        handleValidate(fieldProps.value)
        closePopper()
        fieldProps.onBlur(e)
        break
      default:
    }
  }

  function selectItems(newSelected: Option[]) {
    augProps.setRuntimeError(undefined)
    setSelected(newSelected)
    augProps.setValue(newSelected)
    handleValidate(newSelected)
  }

  function rowClickHandler(value: string) {
    return () => {
      if (isSelectedValue(value, selected)) { // deselect this option
        selectItems(selected.filter(option => option.value !== value))
        return
      }
      const option = selectOption(options, value)
      if (!option) {
        console.error("Couldn't find selected option with value %s", value)
        return
      }
      // not sure if should signal here
      signal()
      selectItems(selected.concat([option]))
    }
  }

  function filterTextChange(e: React.ChangeEvent<HTMLInputElement>) {
    setInput(e.target.value)
  }

  function onDragEnd(result: any) {
    if (!result.destination) {
      return;
    }

    const newSelected = reorder(
      selected,
      result.source.index,
      result.destination.index
    );

    selectItems(newSelected)
  }

  function optionToRow(option: Option, index: number, provided: DraggableProvided, isSelected: boolean) {
    return <TableRow ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    key={option.value}
                    onClick={rowClickHandler(option.value)}
                    hover
                    sx={{ cursor: 'pointer', ...provided.draggableProps.style }}>
        {rowIcon(option.value, index, isSelected)}
        {Object.entries(option.tuple!).map(val => <TableCell key={[val[0], option.value, val[1]].join('_')}>{val[1]}</TableCell>)}
      </TableRow>
  }

  function makeDraggable(option: Option, index: number, isSelected: boolean) {
    return <Draggable key={index} draggableId={index.toString()} index={index} isDragDisabled={!isSelected}>
        {(provided, _snapshot) => optionToRow(option, index, provided, isSelected)}
      </Draggable>
  }

  const columns = getColumns(options.concat(selected))
  const resultProps = {
      loading: loading,
      empty: options.length === 0 && selected.length === 0,
      header: <TableRow><TableCell/>{columns.map(col => <TableCell key={col}>{col}</TableCell>)}</TableRow>,
      body: <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId={info.rpath + '-drop'}
                   renderClone={(provided, _snapshot, rubic) => cloneRow(selected[rubic.source.index], rubic.source.index, provided)}>
          {(provided, _snapshot) =>
            <TableBody {...provided.droppableProps} ref={provided.innerRef}>
              {selected.filter(_ => showSelected)
                .map((option, index) => makeDraggable(option, index, true))
                .concat(!showSelected ? [] : [
                  <Draggable key="divider-drag" draggableId='divider' index={selected.length} isDragDisabled={true}>
                    {(provided, _snapshot) =>
                      <TableRow key="divider"
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                sx={{ backgroundColor: '#f5f5f5', ...provided.draggableProps.style }}>
                        <TableCell colSpan={columns.length + 1}/>
                      </TableRow>}
                  </Draggable>
                ]).concat(options
                  .filter(option => !selected.find(e => e.value === option.value))
                  .map((option, index) => makeDraggable(option, index + selected.length + 1, false)))}
              {provided.placeholder}
            </TableBody>
          }
        </Droppable>
      </DragDropContext>
    }

  const isFocused = document.activeElement?.id == fieldProps.id
  const error = augProps.error
  const selectedExists = selected.length !== 0
  const extraProps = selectedExists ? { onDelete: handleDelete} : {}
  const color = error ? 'error' : (isFocused ? 'primary' : null)

  const selectedLabel = !selectedExists
      ? <Typography display='inline' color={color || '#6b778c'}>{t("select.nothingselected")}</Typography>
      : <Typography display='inline' color={color || 'default'}>{t('select.list', { variables: { nr: selected.length } })}</Typography>
  const label = info.inMultiple
    ? selectedLabel
    : <Stack direction='row' spacing='16px'>
        <Typography color={color || '#6b778c'} display='inline'>
          {fieldProps.label + (fieldProps.required ? ' *' : '')}
        </Typography>
        <Divider orientation="vertical" flexItem />
        {selectedLabel}
      </Stack>

  // TODO: check if zIndex 1200 is ideal or should be lower
  return <>
    <FormControl margin={info.inMultiple ? "none" : "normal"}
                 error={Boolean(error)}
                 onClick={(e) => openPopper(e.currentTarget)}
                 sx={{minWidth: "100%", width: 'max-content'}}>
      <Chip sx={{ minHeight: info.inMultiple ? '40px' : '56px', borderRadius: '5px', justifyContent: 'left' }}
            id={fieldProps.id}
            clickable
            variant='outlined'
            onClick={handleClick}
            label={label}
            color={color || 'default'}
            deleteIcon={<DeleteIcon onMouseDown={(e) => e.preventDefault()} /* prevent receiving focus when clicking delete button*/ />}
            {...extraProps}
            />
      {info.inMultiple ? null : <FormHelperText>{error || ''}</FormHelperText>}
    </FormControl>
    <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{zIndex: 1200}}>
      <ClickAwayListener onClickAway={handleBlur}>
        <Paper elevation={4} sx={{padding: '5 px'}}>
          <Box sx={{ display: 'flex', alignItems: 'center', p: 1, pl: 1.5 }}>
            <SearchIcon sx={{ color: 'action.active', mr: 1, my: 0.5 }} />
            <TextField
                id='filter-options'
                variant='standard'
                hiddenLabel
                // onBlur={handleBlur}
                onFocus={handleFocus}
                value={input}
                onChange={filterTextChange}
                onKeyDown={handleKeyDown}
                inputRef={filterRef}/>
            <Box sx={{paddingLeft: "16px", display: 'flex', flexGrow: 1, justifyContent: 'right'}}>
              <Tooltip placement='right' title={t('select.showselected')}>
                <IconButton onClick={_ => setShowSelected(!showSelected)}>
                  {showSelected ? <Visibility/> : <VisibilityOff/>}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Divider/>
          <Box sx={{ p: 0 }}>
            <Results {...resultProps}/>
          </Box>
        </Paper>
      </ClickAwayListener>
    </Popper>
  </>
}

const InputMultipleEntityFieldLocal = () => (
  <InputError>
    {/* @ts-ignore */}
    <InputMultipleEntityFieldLocalContent />
  </InputError>
)

export default InputMultipleEntityFieldLocal


